/**
 * The canonical {@link RecordCodec} every store in this package writes through.
 *
 * A codec is deliberately the *only* place that knows how to turn a record into
 * text and back. Two things follow from that and both are load-bearing:
 *
 *  - **`futureSchema` is detected here, not at the call site.** The version
 *    travels inside the envelope, so the decoder is the first code that can see
 *    it, and it refuses as a *value* rather than throwing. A caller cannot
 *    accidentally swallow a refusal it is required to return.
 *  - **Per-store validation plugs in rather than forks.** `duplicateIdentity`
 *    and `normalizationRequired` (spec §4.5 rule 7 — manual history must reject
 *    anything that would need decode-time normalization) are properties of one
 *    store's payload shape, not of the encoding. They arrive as
 *    {@link PayloadDecoder}, supplied by the packet that owns the shape, so
 *    adding a store never means editing the kernel.
 */

import type {
  FailClosedTrigger,
  PersistedEnvelope,
  SchemaVersion,
} from "@ff/domain";
import { schemaVersion as brandSchemaVersion } from "@ff/domain";
import type { DecodedRecord, RecordCodec } from "@ff/domain/ports";

import { canonicalJson } from "./canonicalJson";

/**
 * The result of checking one decoded payload against the shape its store
 * requires. A refusal names the trigger the *store* will report, which is why
 * this is not a boolean: `unsupportedShape` and `normalizationRequired` produce
 * different recovery copy and mean different things to the user.
 */
export type PayloadDecode<T> =
  | { readonly ok: true; readonly value: T }
  | {
      readonly ok: false;
      readonly trigger: FailClosedTrigger;
      readonly detail: string;
    };

/**
 * Validates one decoded payload. Receives the version it was stored at, because
 * a store that accepts more than one version has to check the shape it actually
 * found rather than the shape this build writes.
 */
export type PayloadDecoder<T> = (
  payload: unknown,
  version: SchemaVersion,
) => PayloadDecode<T>;

export interface CanonicalCodecOptions<T> {
  /** The version this build writes. Anything above it is refused, never read. */
  readonly currentSchemaVersion: SchemaVersion;
  readonly decodePayload?: PayloadDecoder<T>;
}

/**
 * Accepts any payload. The default for a store that has not yet been given a
 * validator — it is honest about doing no structural checking rather than
 * pretending to, and the stores that need one (P15's canonical effort history,
 * P17's journal entries) supply it.
 */
function acceptAnyPayload<T>(payload: unknown): PayloadDecode<T> {
  return { ok: true, value: payload as T };
}

export function canonicalRecordCodec<T>(
  options: CanonicalCodecOptions<T>,
): RecordCodec<T> {
  const current = options.currentSchemaVersion;
  const decodePayload = options.decodePayload ?? acceptAnyPayload<T>;

  return {
    encode(envelope: PersistedEnvelope<T>): string {
      return canonicalJson(envelope);
    },

    decode(canonical: string): DecodedRecord<T> {
      let parsed: unknown;
      try {
        parsed = JSON.parse(canonical);
      } catch {
        return undecodable("corruptBytes", "not JSON", null, canonical);
      }

      if (parsed === null || typeof parsed !== "object" || Array.isArray(parsed)) {
        return undecodable(
          "unsupportedShape",
          "envelope is not an object",
          null,
          canonical,
        );
      }

      const envelope = parsed as Record<string, unknown>;
      const observed = envelope["schemaVersion"];
      if (typeof observed !== "number" || !Number.isInteger(observed)) {
        return undecodable(
          "unsupportedShape",
          "envelope has no integer schemaVersion",
          null,
          canonical,
        );
      }
      const found = brandSchemaVersion(observed);

      if (!("payload" in envelope)) {
        return undecodable(
          "unsupportedShape",
          "envelope has no payload",
          found,
          canonical,
        );
      }

      // Refuse; never downgrade. A build that read a newer record at its own
      // version would write it back at that version, and the newer build's
      // fields would be gone — a silent, permanent loss disguised as a
      // successful read.
      if (observed > current) {
        return undecodable(
          "futureSchema",
          `record is version ${observed}; this build writes ${current}`,
          found,
          canonical,
        );
      }

      const payload = decodePayload(envelope["payload"], found);
      if (!payload.ok) {
        return undecodable(payload.trigger, payload.detail, found, canonical);
      }

      return { status: "decoded", schemaVersion: found, value: payload.value };
    },
  };
}

function undecodable<T>(
  trigger: FailClosedTrigger,
  detail: string,
  observedSchemaVersion: SchemaVersion | null,
  canonical: string,
): DecodedRecord<T> {
  return {
    status: "undecodable",
    trigger,
    detail,
    observedSchemaVersion,
    // The bytes are still there and untouched; their length is the only honest
    // thing left to report about text this build could not understand.
    preservedByteLength: canonical.length,
  };
}
